// Utility functions for financial data formatting and validation
import { format, parseISO } from 'date-fns';

/**
 * Infer the correct currency symbol for a financial instrument from its
 * symbol pattern, falling back to a fundamentals-reported currency (stocks)
 * when the symbol alone isn't enough.
 *
 * Was previously duplicated (and buggy - defaulted to ₺ for anything that
 * wasn't gold/silver) independently in ImprovedDashboard.tsx and
 * InstrumentDetail.tsx, which is how EUR/USD ended up displayed as "₺1.1778"
 * and AAPL as "₺278.12" despite its own fundamentals correctly reporting USD.
 */
export const getInstrumentCurrencySymbol = (symbol: string, fundamentalsCurrency?: string | null): string => {
  const CURRENCY_SYMBOLS: Record<string, string> = { TRY: '₺', USD: '$', EUR: '€', GBP: '£', CHF: 'CHF' };

  // Currency pairs: XXXYYY=X is quoted in YYY (the second/quote currency)
  if (symbol.endsWith('=X')) {
    const quote = symbol.slice(-5, -2);
    return CURRENCY_SYMBOLS[quote] || quote;
  }
  // Gold/silver futures and crypto (BTC-USD, ETH-USD, ...) are USD-quoted
  if (symbol.includes('GC=F') || symbol.includes('SI=F') || symbol.endsWith('-USD')) {
    return '$';
  }
  // Synthetic Turkish gold (gram_altin, ceyrek_altin, 22_ayar_bilezik, ...)
  if (/altin|bilezik/i.test(symbol)) {
    return '₺';
  }
  // Stocks: prefer the fundamentals-reported currency when known
  if (fundamentalsCurrency && CURRENCY_SYMBOLS[fundamentalsCurrency]) {
    return CURRENCY_SYMBOLS[fundamentalsCurrency];
  }
  // Fallback: BIST stocks (.IS suffix) are TRY, everything else defaults USD
  return symbol.endsWith('.IS') ? '₺' : '$';
};

export type SymbolKind =
  'bist-stock' | 'us-stock' | 'index' | 'gold' | 'crypto' | 'forex' | 'unknown';

/** The one symbol-shape classifier; every symbol-type decision derives from it. */
// The backend's instrument_type cannot serve here: pages need a classification
// before any API data arrives. Futures (GC=F) land in 'forex' via '='; no
// consumer distinguishes the two. URLs arrive in either case; the backend
// uppercases, so match that.
export const classifySymbol = (symbol: string | undefined): SymbolKind => {
  const s = symbol?.toUpperCase() ?? '';
  if (!s) return 'unknown';
  if (s.endsWith('.IS')) return 'bist-stock';
  if (s.includes('=')) return 'forex';
  if (/altin|bilezik/i.test(s)) return 'gold';
  if (s.startsWith('^')) return 'index';
  // A multi-letter quote suffix is a crypto pair; single letters (BRK-B) are US share classes.
  if (/-[A-Z]{2,}$/.test(s)) return 'crypto';
  return /^[A-Z.-]+$/.test(s) ? 'us-stock' : 'unknown';
};

/** Synthetic gold symbols have no instrument row, so no alert can target them. */
export const canCreateAlert = (symbol: string | undefined): boolean =>
  !!symbol && classifySymbol(symbol) !== 'gold';

/** Which movers panel (BIST or US) an instrument page's sidebar should show. */
export const moversMarketFor = (symbol: string | undefined): 'bist' | 'us' => {
  const kind = classifySymbol(symbol);
  return kind === 'us-stock' || kind === 'index' ? 'us' : 'bist';
};

/** Stock-like pages default to daily-close chart ranges and fetch fundamentals. */
// Gold keeps its long-standing stock-like page treatment; only forex and
// crypto have sub-daily realtime charts.
export const isStockLikeSymbol = (symbol: string | undefined): boolean => {
  const kind = classifySymbol(symbol);
  return kind !== 'forex' && kind !== 'crypto';
};

/** Display ticker: THYAO.IS -> THYAO. Anchored, so a mid-string .IS survives. */
export const ticker = (symbol: string): string => symbol.replace(/\.IS$/, '');

// Price formatting utilities
export const formatPrice = (price: number, currency: string = 'USD'): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 4,
  }).format(price);
};

// A fixed decimal floor renders micro-cap coins as zero: SATS at $0.00000001
// shows "$0.00" with toFixed(4). Significant digits keep every price nonzero.
export const formatMicroPrice = (price: number): string =>
  new Intl.NumberFormat('en-US', { maximumSignificantDigits: 4 }).format(price);

export const isMicroPrice = (price: number): boolean => price > 0 && price < 0.01;

export const formatChange = (change: number, isPercent: boolean = false): string => {
  const sign = change >= 0 ? '+' : '';
  const formattedChange = Math.abs(change).toFixed(2);
  return `${sign}${formattedChange}${isPercent ? '%' : ''}`;
};

export const formatVolume = (volume: number): string => {
  if (volume >= 1e9) {
    return `${(volume / 1e9).toFixed(1)}B`;
  } else if (volume >= 1e6) {
    return `${(volume / 1e6).toFixed(1)}M`;
  } else if (volume >= 1e3) {
    return `${(volume / 1e3).toFixed(1)}K`;
  }
  return volume.toString();
};

// Date formatting utilities
export const formatDate = (dateString: string, formatString: string = 'MMM dd, yyyy'): string => {
  try {
    return format(parseISO(dateString), formatString);
  } catch {
    return dateString;
  }
};

export const formatDateTime = (dateString: string): string => {
  return formatDate(dateString, 'MMM dd, yyyy HH:mm');
};

export const formatRelativeTime = (dateString: string): string => {
  const date = parseISO(dateString);
  const now = new Date();
  const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));
  
  if (diffInMinutes < 1) return 'Just now';
  if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
  
  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours < 24) return `${diffInHours}h ago`;
  
  const diffInDays = Math.floor(diffInHours / 24);
  if (diffInDays < 7) return `${diffInDays}d ago`;
  
  return formatDate(dateString);
};

// Validation utilities
export const isValidEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

export const isValidPassword = (password: string): boolean => {
  return password.length >= 8;
};

export const isValidSymbol = (symbol: string): boolean => {
  const symbolRegex = /^[A-Z]{1,10}$/;
  return symbolRegex.test(symbol);
};

// Number utilities
export const clamp = (value: number, min: number, max: number): number => {
  return Math.min(Math.max(value, min), max);
};

export const roundToDecimal = (value: number, decimals: number = 2): number => {
  return Math.round(value * Math.pow(10, decimals)) / Math.pow(10, decimals);
};

// String utilities
export const truncateText = (text: string, maxLength: number): string => {
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
};

export const capitalizeFirst = (text: string): string => {
  return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
};

// Array utilities
export const groupBy = <T, K extends keyof any>(
  array: T[],
  getKey: (item: T) => K
): Record<K, T[]> => {
  return array.reduce((groups, item) => {
    const key = getKey(item);
    if (!groups[key]) {
      groups[key] = [];
    }
    groups[key].push(item);
    return groups;
  }, {} as Record<K, T[]>);
};

// Local storage utilities
export const storage = {
  get: <T>(key: string): T | null => {
    try {
      const item = localStorage.getItem(key);
      return item ? JSON.parse(item) : null;
    } catch {
      return null;
    }
  },
  
  set: <T>(key: string, value: T): void => {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch {
      // Handle storage errors silently
    }
  },
  
  remove: (key: string): void => {
    try {
      localStorage.removeItem(key);
    } catch {
      // Handle storage errors silently
    }
  },
  
  clear: (): void => {
    try {
      localStorage.clear();
    } catch {
      // Handle storage errors silently
    }
  },
};
